import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes as RouterRoutes } from 'react-router-dom';
import ErrorBoundary from 'components/ErrorBoundary';
import ScrollToTop from 'components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import Landing from './pages/landing';

// ── Páginas cargadas on-demand (code splitting) ───────────────────────────────
const Administration                  = lazy(() => import('./pages/administration'));
const BusinessVerificationConfirmation = lazy(() => import('./pages/business-verification-confirmation'));
const Categories                      = lazy(() => import('./pages/categories'));
const CategoryDetail                  = lazy(() => import('./pages/category-detail'));
const IndustryDetail                  = lazy(() => import('./pages/industry-detail'));
const CompanyProfile                  = lazy(() => import('./pages/company-profile'));
const Conversations                   = lazy(() => import('./pages/conversations'));
const ConversationDetail              = lazy(() => import('./pages/conversations/ConversationDetail'));
const CountrySelection                = lazy(() => import('./pages/country-selection'));
const CreateRequirement               = lazy(() => import('./pages/requirements/CreateRequirement'));
const Dashboard                       = lazy(() => import('./pages/dashboard'));
const EmailVerification               = lazy(() => import('./pages/email-verification'));
const LoginPage                       = lazy(() => import('./pages/login'));
const NotFound                        = lazy(() => import('./pages/NotFound'));
const Notifications                   = lazy(() => import('./pages/notifications'));
const PendingAccountScreen            = lazy(() => import('./pages/dashboard/components/PendingAccountScreen'));
const Plans                           = lazy(() => import('./pages/plans'));
const Pricing                         = lazy(() => import('./pages/pricing'));
const PrivacyPolicy                   = lazy(() => import('./pages/privacy'));
const PublishService                  = lazy(() => import('./pages/publish-service'));
const Register                        = lazy(() => import('./pages/register'));
const RequirementDetails              = lazy(() => import('./pages/requirement-details'));
const Requirements                    = lazy(() => import('./pages/requirements'));
const ResetPassword                   = lazy(() => import('./pages/reset-password'));
const SearchResults                   = lazy(() => import('./pages/search-results'));
const Settings                        = lazy(() => import('./pages/settings'));
const TermsOfService                  = lazy(() => import('./pages/terms'));

// Fallback mostrado mientras un chunk de página se descarga
const PageLoader = () => (
    <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
);

export default function ProjectRoutes() {
    return (
        <BrowserRouter>
            <ErrorBoundary>
                <ScrollToTop />
                <Suspense fallback={<PageLoader />}>
                    <RouterRoutes>
                        {/* ── Rutas públicas ─────────────────────────────────────── */}
                        <Route path="/" element={<Landing />} />
                        <Route path="/select-country" element={<CountrySelection />} />
                        <Route path="/country-selection" element={<CountrySelection />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/email-verification" element={<EmailVerification />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/pricing" element={<Pricing />} />

                        {/* ── Cuenta bloqueada (requiere auth, no requiere active) ─ */}
                        <Route
                            path="/plans"
                            element={
                                <ProtectedRoute allowPending>
                                    <Plans />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/pending-approval"
                            element={
                                <ProtectedRoute allowPending>
                                    <PendingAccountScreen />
                                </ProtectedRoute>
                            }
                        />

                        {/* ── Rutas privadas — cualquier usuario activo ───────────── */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <Dashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/requirements"
                            element={
                                <ProtectedRoute>
                                    <Requirements />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/requirements/new"
                            element={
                                <ProtectedRoute>
                                    <CreateRequirement />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/requirements/:id"
                            element={
                                <ProtectedRoute>
                                    <RequirementDetails />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/conversations"
                            element={
                                <ProtectedRoute>
                                    <Conversations />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/conversations/:id"
                            element={
                                <ProtectedRoute>
                                    <ConversationDetail />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/categories"
                            element={
                                <ProtectedRoute>
                                    <Categories />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/categories/:categoryId"
                            element={
                                <ProtectedRoute>
                                    <CategoryDetail />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/industries/:industryId"
                            element={
                                <ProtectedRoute>
                                    <IndustryDetail />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/company-profile"
                            element={
                                <ProtectedRoute>
                                    <CompanyProfile />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/company-profile/:companyId"
                            element={
                                <ProtectedRoute>
                                    <CompanyProfile />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/search-results"
                            element={
                                <ProtectedRoute>
                                    <SearchResults />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/notifications"
                            element={
                                <ProtectedRoute>
                                    <Notifications />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings"
                            element={
                                <ProtectedRoute>
                                    <Settings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/publish-service"
                            element={
                                <ProtectedRoute>
                                    <PublishService />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/business-verification-confirmation"
                            element={
                                <ProtectedRoute>
                                    <BusinessVerificationConfirmation />
                                </ProtectedRoute>
                            }
                        />

                        {/* ── Rutas privadas — solo admin ─────────────────────────── */}
                        <Route
                            path="/administration"
                            element={
                                <ProtectedRoute roles={['admin']}>
                                    <Administration />
                                </ProtectedRoute>
                            }
                        />

                        <Route path="*" element={<NotFound />} />
                    </RouterRoutes>
                </Suspense>
            </ErrorBoundary>
        </BrowserRouter>
    );
}
